'use client'

import { useEffect, useState } from 'react'

import { Amplify } from 'aws-amplify'
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito'
import { CookieStorage } from 'aws-amplify/utils'
import { getCookie } from 'cookies-next'

import { COOKIE_DAYS_TO_EXPIRE } from '@/constants/config'
import { ACCOUNT_SUBDOMAIN } from '@/constants/routes'
import { COOKIE_CONFIG } from '@/lib/cookies/cookie-config'

type AmplifyProviderProps = Readonly<{
  children: React.ReactNode
  subdomain?: string
}>

function AmplifyProvider(props: AmplifyProviderProps) {
  const { subdomain, children } = props
  const [isConfigured, setIsConfigured] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (subdomain === ACCOUNT_SUBDOMAIN) {
      setIsConfigured(true)
      return
    }

    const configureAmplify = async () => {
      try {
        const configCookieName = `amplify-config-${subdomain}`
        const configString = await getCookie(configCookieName)

        if (!configString) {
          throw new Error(`No Amplify config found for subdomain: ${subdomain}`)
        }

        const config = JSON.parse(configString)

        if (!config.userPoolId || !config.clientId) {
          throw new Error('Invalid Amplify config: missing required fields')
        }

        Amplify.configure(
          {
            Auth: {
              Cognito: {
                userPoolId: config.userPoolId,
                userPoolClientId: config.clientId,
              },
            },
          },
          {
            ssr: true,
          },
        )

        // Configure cookie storage - MUST be after Amplify.configure
        const cookieStorage = new CookieStorage({
          ...COOKIE_CONFIG,
          expires: COOKIE_DAYS_TO_EXPIRE,
        })

        cognitoUserPoolsTokenProvider.setKeyValueStorage(cookieStorage)

        setIsConfigured(true)
      } catch (err) {
        console.error('Failed to configure Amplify:', err)
        setError(err instanceof Error ? err.message : 'Configuration failed')
      }
    }

    configureAmplify()
  }, [subdomain])

  if (error) {
    return (
      <div style={{ padding: '2rem', color: 'red' }}>
        <h2>Configuration Error</h2>
        <p>{error}</p>
      </div>
    )
  }

  if (!isConfigured) return null

  return <>{children}</>
}

export default AmplifyProvider
